/**
 * ToothSVGV2 — brand-new premium anatomical tooth SVG set.
 *
 * Four archetype paths (incisor / canine / premolar / molar) with soft cream
 * fills, subtle root shading, and a smooth crown highlight. Condition
 * overlays are dispatched to individual components in ./overlays/ (Caries,
 * RCT, Composite, Amalgam, Implant, Bridge, Missing, Fracture, Crown,
 * Mobility, PeriapicalLesion) so each renders a commercial-quality
 * hand-drawn SVG instead of a coloured box.
 * Selection halo is a rounded orange rectangle matching the reference.
 */
import { OVERLAY_MAP, REPLACES_TOOTH } from "./overlays";
import "./overlays/animations.css";

export function toothType(fdi) {
  const s = String(fdi);
  const pos = Number(s[1]);
  if (pos <= 2) return "incisor";
  if (pos === 3) return "canine";
  if (pos <= 5) return "premolar";
  return "molar";
}

export function toothInfoV2(fdi) {
  const s = String(fdi);
  const q = Number(s[0]);
  const isUpper = q === 1 || q === 2 || q === 5 || q === 6;
  const isRight = q === 1 || q === 4 || q === 5 || q === 8;
  const type = toothType(fdi);
  const names = {
    incisor: ["Central Incisor", "Lateral Incisor"],
    canine: ["Canine"],
    premolar: ["First Premolar", "Second Premolar"],
    molar: ["First Molar", "Second Molar", "Third Molar"],
  };
  const pos = Number(s[1]);
  const idxName = type === "incisor" ? pos - 1
                : type === "premolar" ? pos - 4
                : type === "molar" ? pos - 6 : 0;
  return {
    isUpper,
    quadrantLabel: `${isUpper ? "Upper" : "Lower"} · ${isRight ? "Right" : "Left"}`,
    name: names[type][idxName] || names[type][0],
    type,
  };
}

// Colour system — matches the "high-end dental software" spec (Phase 5).
//   Healthy          → soft green
//   Diagnosis        → warm red
//   Restoration      → royal blue
//   RCT              → deep blue
//   Implant          → titanium grey
//   Extraction/miss  → dark grey
//   Pending          → orange
//   Completed        → green
export const CHART_COLORS = {
  healthy:     "#3F7A4A",
  diagnosis:   "#B94A48",
  restoration: "#2C58A6",
  rct:         "#173A73",
  implant:     "#8892A6",
  extraction:  "#3F4A54",
  pending:     "#D06A42",
  completed:   "#3F7A4A",
};

// Condition metadata — expanded to match the four right-panel categories.
// Each entry declares which colour token drives its overlay + status pill.
export const CONDITION_META_V2 = {
  // ---- Diagnosis (warm red) ----
  caries:     { label: "Caries",       color: CHART_COLORS.diagnosis,   category: "diagnosis" },
  fracture:   { label: "Fracture",     color: CHART_COLORS.diagnosis,   category: "diagnosis" },
  attrition:  { label: "Attrition",    color: CHART_COLORS.diagnosis,   category: "diagnosis" },
  mobility:   { label: "Mobility",     color: CHART_COLORS.diagnosis,   category: "diagnosis" },
  wear:       { label: "Wear",         color: CHART_COLORS.diagnosis,   category: "diagnosis" },
  hypoplasia: { label: "Hypoplasia",   color: CHART_COLORS.diagnosis,   category: "diagnosis" },
  // ---- Restoration (royal blue) ----
  composite:  { label: "Composite",    color: CHART_COLORS.restoration, category: "restoration" },
  gic:        { label: "GIC",          color: CHART_COLORS.restoration, category: "restoration" },
  amalgam:    { label: "Amalgam",      color: "#8892A6",                category: "restoration" },
  crown:      { label: "Crown",        color: "#B58834",                category: "restoration" },
  veneer:     { label: "Veneer",       color: CHART_COLORS.restoration, category: "restoration" },
  onlay:      { label: "Onlay",        color: CHART_COLORS.restoration, category: "restoration" },
  filling:    { label: "Filling",      color: CHART_COLORS.restoration, category: "restoration" },
  sealant:    { label: "Sealant",      color: CHART_COLORS.restoration, category: "restoration" },
  // ---- Endodontic (deep blue) ----
  rct:        { label: "Root Canal",   color: CHART_COLORS.rct,         category: "endodontic" },
  pulpotomy:  { label: "Pulpotomy",    color: CHART_COLORS.rct,         category: "endodontic" },
  pulpectomy: { label: "Pulpectomy",   color: CHART_COLORS.rct,         category: "endodontic" },
  // ---- Prosthodontics (titanium grey + gold accents) ----
  implant:    { label: "Implant",      color: CHART_COLORS.implant,     category: "prosthodontics" },
  bridge:     { label: "Bridge",       color: "#8A6E4E",                category: "prosthodontics" },
  pontic:     { label: "Pontic",       color: "#8A6E4E",                category: "prosthodontics" },
  abutment:   { label: "Abutment",     color: "#8A6E4E",                category: "prosthodontics" },
  // ---- Terminal states (dark grey) ----
  extraction: { label: "Extraction",   color: CHART_COLORS.extraction,  category: "terminal" },
  missing:    { label: "Missing",      color: CHART_COLORS.extraction,  category: "terminal" },
};

// FDI numbering rows for the arch
export const PERM_UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
export const PERM_LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];
export const PRIM_UPPER = [55, 54, 53, 52, 51, 61, 62, 63, 64, 65];
export const PRIM_LOWER = [85, 84, 83, 82, 81, 71, 72, 73, 74, 75];

// ---- Per-type crown + root paths ------------------------------------------
// All draw in a 60×130 viewBox with crown centered horizontally on x=30 and
// occupying y ∈ [20, 90], root ∈ [90, 128]. Colours match the warm cream
// palette from the reference.
const CROWN_FILL = "#F8EFDF";
const CROWN_STROKE = "#C5B58C";
const ROOT_FILL = "#EFE1C5";

function CrownIncisor({ opacity = 1 }) {
  return (
    <>
      <path
        d="M17 30 C17 24 23 20 30 20 C37 20 43 24 43 30 L44 78 C44 86 37 90 30 90 C23 90 16 86 16 78 Z"
        fill={CROWN_FILL} stroke={CROWN_STROKE} strokeWidth="1.4" opacity={opacity} />
      <path d="M23 32 C25 28 35 28 37 32" fill="none" stroke="#E7D8B6" strokeWidth="1" opacity={opacity} />
    </>
  );
}
function CrownCanine({ opacity = 1 }) {
  return (
    <>
      <path
        d="M17 32 C17 26 22 21 30 20 C38 21 43 26 43 32 L46 80 C46 87 38 90 30 90 C22 90 14 87 14 80 Z"
        fill={CROWN_FILL} stroke={CROWN_STROKE} strokeWidth="1.4" opacity={opacity} />
      <path d="M27 27 L30 20 L33 27" fill="none" stroke={CROWN_STROKE} strokeWidth="1" opacity={opacity} />
    </>
  );
}
function CrownPremolar({ opacity = 1 }) {
  return (
    <>
      <path
        d="M14 40 C14 30 21 22 30 22 C39 22 46 30 46 40 L47 78 C47 86 39 90 30 90 C21 90 13 86 13 78 Z"
        fill={CROWN_FILL} stroke={CROWN_STROKE} strokeWidth="1.4" opacity={opacity} />
      <ellipse cx={24} cy={32} rx={4} ry={5} fill="#F1E4C4" opacity={opacity * 0.7} />
      <ellipse cx={36} cy={32} rx={4} ry={5} fill="#F1E4C4" opacity={opacity * 0.7} />
    </>
  );
}
function CrownMolar({ opacity = 1 }) {
  return (
    <>
      <path
        d="M10 42 C10 30 18 22 30 22 C42 22 50 30 50 42 L51 78 C51 87 42 91 30 91 C18 91 9 87 9 78 Z"
        fill={CROWN_FILL} stroke={CROWN_STROKE} strokeWidth="1.4" opacity={opacity} />
      {/* cusps */}
      <ellipse cx={20} cy={34} rx={5} ry={6} fill="#F1E4C4" opacity={opacity * 0.7} />
      <ellipse cx={40} cy={34} rx={5} ry={6} fill="#F1E4C4" opacity={opacity * 0.7} />
      <ellipse cx={30} cy={54} rx={12} ry={4} fill="#F1E4C4" opacity={opacity * 0.5} />
    </>
  );
}

function RootSingle({ opacity = 1 }) {
  return (
    <path d="M22 90 L20 128 C20 130 40 130 40 128 L38 90 Z"
          fill={ROOT_FILL} stroke={CROWN_STROKE} strokeWidth="1.2" opacity={opacity} />
  );
}
function RootDouble({ opacity = 1 }) {
  return (
    <>
      <path d="M17 90 L14 126 C14 129 26 129 26 126 L28 90 Z"
            fill={ROOT_FILL} stroke={CROWN_STROKE} strokeWidth="1.2" opacity={opacity} />
      <path d="M43 90 L46 126 C46 129 34 129 34 126 L32 90 Z"
            fill={ROOT_FILL} stroke={CROWN_STROKE} strokeWidth="1.2" opacity={opacity} />
    </>
  );
}
function RootTriple({ opacity = 1 }) {
  return (
    <>
      <path d="M12 90 L10 122 C10 125 22 125 22 122 L24 90 Z"
            fill={ROOT_FILL} stroke={CROWN_STROKE} strokeWidth="1.2" opacity={opacity} />
      <path d="M48 90 L50 122 C50 125 38 125 38 122 L36 90 Z"
            fill={ROOT_FILL} stroke={CROWN_STROKE} strokeWidth="1.2" opacity={opacity} />
      <path d="M27 90 L28 128 C28 130 32 130 32 128 L33 90 Z"
            fill={ROOT_FILL} stroke={CROWN_STROKE} strokeWidth="1.2" opacity={opacity} />
    </>
  );
}

// ---- Condition overlays (dispatched from ./overlays/) --------------------
function Overlays({ conditions, type, surfaces = [], toothId }) {
  return (
    <g>
      {/* Selected-surface highlights (transient UI hint) */}
      {surfaces.includes("O") && (
        <rect x={14} y={50} width={32} height={18} rx={4}
              fill="#D06A42" fillOpacity={0.18} stroke="#D06A42" strokeWidth={1} />
      )}
      {surfaces.includes("M") && <rect x={9}  y={30} width={5} height={40} fill="#D06A42" fillOpacity={0.25} />}
      {surfaces.includes("D") && <rect x={46} y={30} width={5} height={40} fill="#D06A42" fillOpacity={0.25} />}
      {surfaces.includes("B") && <rect x={14} y={30} width={32} height={5} fill="#D06A42" fillOpacity={0.25} />}
      {surfaces.includes("L") && <rect x={14} y={80} width={32} height={5} fill="#D06A42" fillOpacity={0.25} />}

      {/* Real condition overlays dispatched to their own SVG components */}
      {(conditions || []).map((c, i) => {
        const Comp = OVERLAY_MAP[c.code];
        if (!Comp) return null;
        return (
          <g key={c.id || `${c.code}-${i}`} className="viyora-overlay-in">
            <Comp
              type={type}
              surfaces={c.surfaces || []}
              grade={c.grade}
              toothId={`${toothId}-${i}`}
            />
          </g>
        );
      })}
    </g>
  );
}

// ---- Main component -------------------------------------------------------
export default function ToothV2({
  tooth,
  isSelected = false,
  selectedSurfaces = [],
  conditions = [],
  onSelect,
  hovered = false,
}) {
  const info = toothInfoV2(tooth);
  const type = info.type;
  const codes = new Set((conditions || []).map((c) => c.code));
  const replaced = [...codes].some((c) => REPLACES_TOOTH.has(c));
  const missing = codes.has("missing") || codes.has("extraction");

  const Root = type === "molar" ? RootTriple
             : type === "premolar" ? RootDouble
             : RootSingle;
  const Crown = type === "incisor" ? CrownIncisor
              : type === "canine"   ? CrownCanine
              : type === "premolar" ? CrownPremolar
              : CrownMolar;

  return (
    <button
      type="button"
      onClick={(e) => onSelect?.(String(tooth), e)}
      data-testid={`toothv2-${tooth}`}
      data-tooth={tooth}
      data-selected={isSelected ? "true" : "false"}
      title={`${tooth} · ${info.name}`}
      className={`viyora-tooth-btn group relative focus:outline-none transition-transform duration-200 ${
        isSelected ? "scale-105" : "hover:scale-[1.04]"
      }`}
    >
      <svg viewBox="0 0 60 130" width="46" height="98" className="overflow-visible">
        {/* Selection halo — pulsating orange outline */}
        {isSelected && (
          <rect className="viyora-halo"
                x={2} y={4} width={56} height={122} rx={10} ry={10}
                fill="#FFF6F0" stroke="#D06A42" strokeWidth={1.5} />
        )}
        {/* Natural tooth — hidden entirely when replaced by implant/missing */}
        {!replaced && (
          <>
            <Root  opacity={missing ? 0.25 : 1} />
            <Crown opacity={missing ? 0.25 : 1} />
          </>
        )}
        <Overlays conditions={conditions} type={type}
                  surfaces={selectedSurfaces} toothId={tooth} />
      </svg>
    </button>
  );
}
